import { ApiProperty } from '@nestjs/swagger'
import {
  IsNotEmpty,
  IsUUID,
  IsEnum,
  IsString,
  IsNumber,
  Min,
  Max,
  IsDateString,
  IsOptional,
} from 'class-validator'
import { PedagogicContentState } from '@sphere/shared/domain'
import {
  MAX_WEIGHT,
  MAX_WORKLOAD,
  MIN_WEIGHT,
  MIN_WORKLOAD,
} from '@sphere/school/domain'

export class ValidateStudentTeachingDto {
  @ApiProperty({
    type: 'string',
    description: "ID de l'enseignement",
    example: '550e8400-e29b-41d4-a716-446655440000',
  })
  @IsNotEmpty()
  @IsUUID('4')
  id!: string

  @ApiProperty({
    type: 'string',
    description: 'ID de la classe',
    example: '550e8400-e29b-41d4-a716-446655440000',
  })
  @IsNotEmpty()
  @IsUUID('4')
  classId?: string

  @ApiProperty({
    type: 'string',
    description: 'ID du groupe',
    example: '550e8400-e29b-41d4-a716-446655440000',
  })
  @IsNotEmpty()
  @IsUUID('4')
  groupId?: string

  @ApiProperty({
    description: 'État',
    enum: PedagogicContentState,
    example: PedagogicContentState.ACTIVE,
  })
  @IsNotEmpty()
  @IsEnum(PedagogicContentState)
  state!: PedagogicContentState

  @ApiProperty({
    type: 'string',
    description: "ID du type d'enseignement",
    example: '550e8400-e29b-41d4-a716-446655440000',
  })
  @IsNotEmpty()
  @IsUUID('4')
  typeId!: string

  @ApiProperty({
    type: 'string',
    description: "ID de l'année scolaire",
    example: '550e8400-e29b-41d4-a716-446655440000',
  })
  @IsNotEmpty()
  @IsUUID('4')
  schoolYearId!: string

  @ApiProperty({
    type: 'string',
    description: "Code de l'année scolaire",
    example: '2024-2025',
  })
  @IsNotEmpty()
  @IsString()
  schoolYearCode!: string

  @ApiProperty({
    type: 'string',
    description: 'Description',
    example: "Description de l'enseignement",
  })
  @IsNotEmpty()
  @IsString()
  description!: string

  @ApiProperty({
    type: Number,
    description: 'Poids dans la progression',
    example: 10,
  })
  @IsNotEmpty()
  @IsNumber()
  @Min(MIN_WEIGHT)
  @Max(MAX_WEIGHT)
  weightInProgression!: number

  @ApiProperty({
    type: Number,
    description: 'Charge de travail estimée',
    example: 10,
  })
  @IsNotEmpty()
  @IsNumber()
  @Min(MIN_WORKLOAD)
  @Max(MAX_WORKLOAD)
  estimatedWorkload!: number

  @ApiProperty({
    type: 'string',
    description: 'ID du créateur',
    example: '550e8400-e29b-41d4-a716-446655440000',
  })
  @IsNotEmpty()
  @IsString()
  createdByUserId!: string

  @ApiProperty({
    type: 'string',
    description: 'Date de création',
    example: '2024-09-01',
  })
  @IsNotEmpty()
  @IsDateString()
  createdAt!: Date

  @ApiProperty({
    type: 'string',
    description: 'Date de mise à jour',
    example: '2024-09-01',
  })
  @IsNotEmpty()
  @IsDateString()
  updatedAt!: Date

  @ApiProperty({
    type: 'string',
    description: 'Date de suppression',
    example: '2024-09-01',
  })
  @IsOptional()
  @IsDateString()
  deletedAt?: Date

  @ApiProperty({
    type: 'string',
    description: "ID de l'utilisateur qui a supprimé",
    example: '550e8400-e29b-41d4-a716-446655440000',
  })
  @IsOptional()
  @IsString()
  deletedByUserId?: string

  @ApiProperty({
    type: 'string',
    description: "ID de l'établissement",
    example: '550e8400-e29b-41d4-a716-446655440000',
  })
  @IsNotEmpty()
  @IsUUID('4')
  establishmentId!: string

  @ApiProperty({
    type: 'string',
    description: "Label de l'établissement",
    example: 'Etablissement de test',
  })
  @IsNotEmpty()
  @IsString()
  establishmentLabel!: string

  @ApiProperty({
    type: 'string',
    description: 'ID du programme version',
    example: '550e8400-e29b-41d4-a716-446655440000',
  })
  @IsNotEmpty()
  @IsUUID('4')
  programVersionId!: string

  @ApiProperty({
    type: 'string',
    description: 'ID de la matière',
    example: '550e8400-e29b-41d4-a716-446655440000',
  })
  @IsNotEmpty()
  @IsUUID('4')
  subjectExternalId!: string

  @ApiProperty({
    type: 'string',
    description: 'Label de la matière',
    example: 'Mathématiques',
  })
  @IsNotEmpty()
  @IsString()
  subjectLabel!: string

  @ApiProperty({
    type: 'string',
    description: 'ID du niveau pédagogique',
    example: '550e8400-e29b-41d4-a716-446655440000',
  })
  @IsNotEmpty()
  @IsUUID('4')
  levelExternalId!: string

  @ApiProperty({
    type: 'string',
    description: 'Label du niveau pédagogique',
    example: 'Niveau 1',
  })
  @IsNotEmpty()
  @IsString()
  levelLabel!: string

  @ApiProperty({
    type: Number,
    description: 'Nombre de chapitres',
    example: 10,
  })
  @IsNotEmpty()
  @IsNumber()
  chaptersCount!: number

  @ApiProperty({
    type: Number,
    description: 'Nombre de leçons',
    example: 10,
  })
  @IsNotEmpty()
  @IsNumber()
  lessonsCount!: number

  @ApiProperty({
    type: Number,
    description: 'Nombre de ressources',
    example: 10,
  })
  @IsNotEmpty()
  @IsNumber()
  resourcesCount!: number

  @ApiProperty({
    type: Number,
    description: 'Nombre de staff',
    example: 10,
  })
  @IsNotEmpty()
  @IsNumber()
  staffCount!: number
}
